import { ApiError } from "@/shared/lib/api/http"
import { SHARED_API_ERROR_NS, type SharedApiErrorCode } from "@/shared/lib/api-errors/api-error-codes"

/**
 * A translator that returns the key itself on a miss. This matches the tenant i18n behavior
 * (`useTranslations(...)` returns the scoped key string when no message exists), so callers pass the
 * UN-namespaced root translator (`useTranslations()`) and the resolver reads any namespace it needs.
 */
export type ErrorTranslator = (key: string) => string

export type ResolveApiErrorOptions = {
  /** Feature error namespace to try first, e.g. `"finance"` tries `finance.errors.<CODE>`. */
  domain?: string
}

/** A machine code is UPPER_SNAKE_CASE. Used to detect codes embedded in `issues.formErrors`. */
const CODE_SHAPE = /^[A-Z][A-Z0-9_]+$/

/**
 * Map an HTTP status to a shared generic code. Other 4xx are treated as input/validation problems;
 * 5xx and network failures are NOT the user's input.
 */
export function statusToGenericCode(status: number): SharedApiErrorCode {
  switch (status) {
    case 401:
      return "UNAUTHENTICATED"
    case 403:
      return "FORBIDDEN"
    case 404:
      return "NOT_FOUND"
    case 408:
      return "TIMEOUT"
    case 409:
      return "CONFLICT"
    case 410:
      return "GONE"
    case 413:
      return "PAYLOAD_TOO_LARGE"
    case 429:
      return "RATE_LIMITED"
    case 400:
    case 422:
      return "VALIDATION_ERROR"
    default:
      if (status >= 500) return "UNEXPECTED"
      if (status <= 0) return "NETWORK"
      return "VALIDATION_ERROR"
  }
}

/**
 * Candidate machine codes for an ApiError, in priority order: the explicit `code`, then any
 * code-shaped tokens in `issues.formErrors`. NEVER reads `error.message` (it is the raw backend
 * string already concatenated with formErrors and must not reach the user).
 */
export function extractApiErrorCodes(error: ApiError): string[] {
  const codes: string[] = []
  const explicit = typeof error.code === "string" ? error.code.trim() : ""
  if (explicit && CODE_SHAPE.test(explicit)) codes.push(explicit)

  const formErrors = error.issues?.formErrors
  if (Array.isArray(formErrors)) {
    for (const line of formErrors) {
      if (typeof line !== "string") continue
      const trimmed = line.trim()
      if (CODE_SHAPE.test(trimmed) && !codes.includes(trimmed)) codes.push(trimmed)
    }
  }
  return codes
}

/** First key whose translation is present (i.e. `t(key) !== key`); null if none resolve. */
function firstTranslated(t: ErrorTranslator, keys: string[]): string | null {
  for (const key of keys) {
    const value = t(key)
    if (typeof value === "string" && value.length > 0 && value !== key) return value
  }
  return null
}

function genericMessage(t: ErrorTranslator, code: SharedApiErrorCode): string {
  // `UNEXPECTED` is contract-tested to exist in every locale, so the `?? key` branch is unreachable.
  return firstTranslated(t, [`${SHARED_API_ERROR_NS}.${code}`, `${SHARED_API_ERROR_NS}.UNEXPECTED`]) ?? `${SHARED_API_ERROR_NS}.UNEXPECTED`
}

/**
 * Resolve a user-facing, localized message for any thrown value. This is the single source of API
 * error copy for the tenant app. It NEVER returns the raw backend message: it maps error codes to
 * i18n (feature namespace, then shared `errors.api.*`), else a status-based generic, else a final
 * generic.
 */
export function resolveApiErrorMessage(error: unknown, t: ErrorTranslator, options: ResolveApiErrorOptions = {}): string {
  // Not an API error at all (network failure, timeout, parse error) — not the user's input.
  if (!(error instanceof ApiError)) {
    return genericMessage(t, "NETWORK")
  }

  const { domain } = options
  for (const code of extractApiErrorCodes(error)) {
    const keys = domain ? [`${domain}.errors.${code}`, `${SHARED_API_ERROR_NS}.${code}`] : [`${SHARED_API_ERROR_NS}.${code}`]
    const message = firstTranslated(t, keys)
    if (message) return message
  }

  return genericMessage(t, statusToGenericCode(error.status))
}
